/* eslint-disable eqeqeq */
import React from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { SESSION_STATUS, SESSION_TYPE } from "../constants";
import { updateSession } from "../../../Redux/Slices/sessions.slice";
import getSessionTextType from "../getSessionTextType";
import { getAssetTypeName } from "../../PowerAfrica/getAssetType";
import { POWER_AFRICA_ASSETS } from "../../PowerAfrica/constants";
import getFormatedFloat from "../../../Utils/getFormatedFloat";
import useRule from "../../../Core/hooks/useRule";
import { TENANT_RULE_VALUE } from "../../Login/constants";

export const useSessionCardData = () => {
  const { currentSession } = useSelector((state) => state.sessions);

  const getEstimatedCost = () => {
    const total =
      parseFloat(currentSession?.estimatedCostPerSqm) *
      parseFloat(currentSession?.estimatedArea);

    return getFormatedFloat(total.toFixed(2));
  };

  const getEstimatedTitle = () => {
    if (currentSession?.activityType === SESSION_TYPE.TILLING)
      return "Estimated Area";
    if (currentSession?.activityType === SESSION_TYPE.WATER_PUMPING)
      return "Estimated Litres";
    return "Estimated Weight";
  };

  return [
    {
      title: "Activity Type",
      value: getSessionTextType(currentSession?.activityType),
    },
    {
      title: "Date",
      value: dayjs(currentSession?.activityDate).format("MMM DD, YYYY") || "--",
    },
    {
      title: "Start Time",
      value: <p className="uppercase">{currentSession?.timeSlot || "--"}</p>,
    },
    {
      title: getEstimatedTitle(),
      value: currentSession?.estimatedArea
        ? `${getFormatedFloat(currentSession?.estimatedArea)} ${
            currentSession?.unit
          }`
        : "--",
    },
    {
      title: `Estimated Cost/${currentSession?.unit}`,
      value: currentSession?.estimatedCostPerSqm
        ? `${getFormatedFloat(currentSession?.estimatedCostPerSqm)} KSH`
        : "--",
    },
    {
      title: "Total Estimated Cost",
      value: currentSession?.estimatedArea ? `${getEstimatedCost()} KSH` : "--",
    },
    {
      title: "Farmer Name",
      value: currentSession?.farmerFirstName
        ? `${currentSession?.farmerFirstName} ${currentSession?.farmerLastName}`
        : "--",
    },
    {
      title: "Location",
      value:
        currentSession?.farmLat && currentSession?.farmLong
          ? `${currentSession.farmLat}, ${currentSession.farmLong}`
          : "--",
    },
    {
      title: "Hub Station",
      value: <p className="capitalize">{currentSession?.hubName}</p>,
    },
  ];
};

export const useAssetCardData = ({ sessionChecklist = [] }) => {
  const dispatch = useDispatch();

  const { currentSession } = useSelector((state) => state.sessions);

  const { checklists } = useSelector((state) => state.powerAfrica);

  const hasChecklistAccess = useRule([TENANT_RULE_VALUE.KNOWN_ISSUES]);

  // find the passed checklist items
  const checklistPassed = sessionChecklist?.filter(
    (item) => item?.passed === 1,
  );

  // find the total length of checklist items -> dependent on asset type
  const getChecklistLength = () => {
    if (currentSession?.assetType == POWER_AFRICA_ASSETS.TILLER)
      return checklists?.tiller?.length;
    if (currentSession?.assetType == POWER_AFRICA_ASSETS.MILLER)
      return checklists?.miller?.length;
    if (currentSession?.assetType == POWER_AFRICA_ASSETS.THRESHER)
      return checklists?.thresher?.length;
    if (currentSession?.assetType == POWER_AFRICA_ASSETS.WATER_PUMP)
      return checklists?.waterPump?.length;
    if (currentSession?.assetType == POWER_AFRICA_ASSETS.CHAUF_CUTTER)
      return checklists?.chaufCutter?.length;
    return "--";
  };

  const data = [
    {
      title: "Type",
      value: getAssetTypeName(currentSession?.assetType) || "--",
    },
    {
      title: "ID",
      value: currentSession?.assetRegistrationNo || "--",
    },
    {
      title: "Safety Checklist",
      value:
        currentSession.status === SESSION_STATUS.UPCOMING
          ? "--"
          : `${checklistPassed?.length}/${getChecklistLength()}`,

      onClick:
        currentSession.status === SESSION_STATUS.UPCOMING
          ? null
          : () =>
              dispatch(updateSession({ key: "checklistModal", value: true })),
    },
  ];

  if (!currentSession.batteries.length) {
    data.push({
      title: "Batteries",
      value: "Not Assigned",
    });

    return data;
  }

  currentSession.batteries.forEach((item, index) => {
    data.push({
      title: `Battery ${index + 1}`,
      value: item.registrationNo,
    });
  });

  return hasChecklistAccess
    ? data
    : data.filter((d) => d.title !== "Safety Checklist");
};

export const useAgentCardData = () => {
  const { currentSession } = useSelector((state) => state.sessions);

  return [
    {
      title: "Name",
      value: currentSession?.agentFirstName
        ? `${currentSession?.agentFirstName} ${currentSession?.agentLastName}`
        : "--",
    },
    {
      title: "Role",
      value: (
        <p className="capitalize">{currentSession?.agentRoleName || "--"}</p>
      ),
    },
  ];
};

export const useSessionSummaryData = () => {
  const { currentSession } = useSelector((state) => state.sessions);

  if (currentSession.status !== SESSION_STATUS.COMPLETED) {
    return [];
  }

  const getTotalCost = () => {
    const total =
      parseFloat(currentSession?.actualArea) *
      parseFloat(currentSession?.actualCostPerSqm);

    return getFormatedFloat(total.toFixed(2));
  };

  const getActualAreaTrend = () => {
    // actual area was more
    if (
      parseFloat(currentSession?.actualArea) >
      parseFloat(currentSession?.estimatedArea)
    )
      return "up";

    // actual area was less
    if (
      parseFloat(currentSession?.actualArea) <
      parseFloat(currentSession?.estimatedArea)
    )
      return "down";

    // equal area
    return false;
  };

  const getActualCostTrend = () => {
    // actual cost was more
    if (
      parseFloat(currentSession?.actualTotalCost) >
      parseFloat(currentSession?.estimatedTotalCost)
    )
      return "up";

    // actual cost was less
    if (
      parseFloat(currentSession?.actualTotalCost) <
      parseFloat(currentSession?.estimatedTotalCost)
    )
      return "down";

    // equal cost
    return false;
  };

  const getTotalActualTitle = () => {
    if (currentSession?.activityType === SESSION_TYPE.TILLING)
      return "Total Actual Area";
    if (currentSession?.activityType === SESSION_TYPE.WATER_PUMPING)
      return "Total Actual Litres";
    return "Total Actual Weight";
  };

  return [
    {
      title: "Batteries Used",
      value: currentSession?.batteries?.length || "--",
    },
    {
      title: "Duration",
      value: `${dayjs(currentSession?.startTime).format("hh:mm A")} - ${dayjs(
        currentSession?.endTime,
      ).format("hh:mm A")}`,
    },
    {
      title: "Location",
      value: `${currentSession?.farmLat}, ${currentSession?.farmLong}`,
    },
    {
      title: getTotalActualTitle(),
      value: `${getFormatedFloat(currentSession?.actualArea)} ${
        currentSession?.unit
      }`,
      trend: getActualAreaTrend(),
    },
    {
      title: "Total Actual Cost",
      value: `${getTotalCost()} KSH`,
      trend: getActualCostTrend(),
    },
    {
      title: "MPESA Reference",
      value: currentSession?.mpesa || "--",
    },
  ];
};

export const useCostCalculationData = (driverCommission) => {
  const { currentSession } = useSelector((state) => state.sessions);

  const batteryRevenue = driverCommission?.batteryRevenue || [];

  const getEnergyConsumed = () => {
    if (!batteryRevenue?.length) return "--";

    const total = batteryRevenue?.reduce((acc, current) => {
      acc += parseFloat(current?.usage || 0);
      return acc;
    }, 0);

    if (Number.isNaN(total)) return "--";
    return `${parseFloat(total).toFixed(1)} kWh`;
  };

  const getEnergyCost = () => {
    if (!batteryRevenue?.length) return "--";

    const total = batteryRevenue?.reduce((acc, current) => {
      acc += parseFloat(current?.cost || 0);
      return acc;
    }, 0);

    if (Number.isNaN(total)) return "--";
    return `${parseFloat(total).toFixed(0)} KSH`;
  };

  if (currentSession.status !== SESSION_STATUS.COMPLETED) {
    return [];
  }

  const getTotalCost = () => {
    const session = parseFloat(driverCommission?.amount || 0);

    const battery = batteryRevenue?.reduce((acc, current) => {
      acc += parseFloat(current?.cost || 0);
      return acc;
    }, 0);

    const total = parseFloat(session) - parseFloat(battery);

    if (Number.isNaN(total)) return "--";
    return `${parseFloat(total).toFixed(0)} KSH`;
  };

  return [
    {
      //  title: "Actual Area Covered",
      // value: currentSession?.actualArea
      //   ? `${currentSession?.actualArea} ${currentSession?.unit}`
      //   : "--",
      title: "Session Duration",
      value: driverCommission?.sessionTime
        ? `${driverCommission?.sessionTime}h`
        : "--",
    },
    {
      title: "Energy Consumed",
      value: getEnergyConsumed(),
    },
    {
      // title: "Cost of Activity",
      // value: currentSession?.totalCost
      //   ? `${parseFloat(currentSession?.totalCost).toFixed(0)} KSH`
      //   : "--",
      title: "Agent Commission",
      value: driverCommission?.amount
        ? `${parseFloat(driverCommission?.amount).toFixed(0)} KSH`
        : "--",
    },
    {
      title: "Energy Cost (Deducted)",
      value: getEnergyCost(),
    },
    {
      title: "Total Cost",
      value: getTotalCost(),
    },
  ];
};
